import { isEqual, omit } from "lodash";

/**
 * Processes and compares two arrays of objects (new and existing) to determine
 * which objects have been added, modified, deleted, or remain unchanged.
 *
 * @param newObjects - The array of new objects to compare. Defaults to an empty array.
 * @param existingObjects - The array of existing objects to compare against. Defaults to an empty array.
 * @param newObjKeyExtractor - A function to extract a unique key from each object in the `newObjects` array.
 * @param existingObjKeyExtractor - A function to extract a unique key from each object in the `existingObjects` array.
 * @param retainedFields - A function that determines which fields from the existing object should be retained in the new object.
 *                          It takes the existing object and the new object as arguments and returns an object containing the retained fields.
 * @param fieldsToDelete - An array of field names that should be removed from the new objects if they are not present in the existing objects.
 *                         Defaults to an empty array.
 * 
 * @returns An object containing the following properties:
 * - `modifiedObjects`: An array of objects that have been modified (i.e., they exist in both arrays but have differences).
 * - `deletedObjects`: An array of keys representing objects that exist in the `existingObjects` array but not in the `newObjects` array.
 * - `addedObjects`: An array of objects that exist in the `newObjects` array but not in the `existingObjects` array.
 * - `unchangedObjects`: An array of objects that exist in both arrays and have no differences.
 *
 * @example
 * const newObjects = [{ id: '1', name: 'Alice' }, { id: '2', name: 'Bob' }];
 * const existingObjects = [{ id: '1', name: 'Alice' }, { id: '3', name: 'Charlie' }];
 * const result = compareObjects(
 *   newObjects,
 *   existingObjects,
 *   obj => obj.id,
 *   obj => obj.id,
 *   (existingObj, newObj) => ({ lastUpdated: existingObj.lastUpdated }),
 *   ['temporaryField']
 * );
 * console.log(result);
 * // {
 * //   modifiedObjects: [],
 * //   deletedObjects: ['3'],
 * //   addedObjects: [{ id: '2', name: 'Bob' }],
 * //   unchangedObjects: [{ id: '1', name: 'Alice' }]
 * // }
 */
export const compareObjects = (
    newObjects: Record<string, any>[] = [],
    existingObjects: Record<string, any>[] = [],
    newObjKeyExtractor: (obj: Record<string, any>) => string,
    existingObjKeyExtractor: (obj: Record<string, any>) => string,
    retainedFields: (existingObj: Record<string, any>, newObj: Record<string, any>) => Record<string, any>,
    fieldsToDelete: string[] = []
) => {
    const modifiedObjects: Record<string, any>[] = [];
    const deletedObjects: string[] = [];
    const addedObjects: Record<string, any>[] = [];
    const unchangedObjects: Record<string, any>[] = [];

    // Check if both newObjects and existingObjects arrays are non-empty
    if (!newObjects.length && !existingObjects.length) {
        return { modifiedObjects, deletedObjects, addedObjects, unchangedObjects };
    }
    // Create a Map of existing objects using their unique keys for quick lookup
    const existingMap = new Map(
        existingObjects.map(obj => [existingObjKeyExtractor(obj), obj])
    );

    for (const newObj of newObjects) {

        // Extract the unique key for the current new object
        const key = newObjKeyExtractor(newObj);
        // Retrieve the corresponding existing object using the key
        const existingObj = existingMap.get(key);

        if (existingObj) {
            // Remove the matched existing object from the Map
            existingMap.delete(key);

            const retained = retainedFields(existingObj, newObj);
            // Merge retained fields from the existing object into the new object
            const merged = { ...newObj, ...retained };

            // Filter out fieldsToDelete only if they’re not in the existing object
            const fieldsToActuallyDelete = fieldsToDelete.filter(
                field => !hasNestedField(existingObj, field)
            );

            const updatedNewObj = omit(merged, fieldsToActuallyDelete);
            // Compare the existing object and the updated new object for differences
            if (!isEqual(existingObj, updatedNewObj)) {
                modifiedObjects.push(updatedNewObj);
            } else {
                unchangedObjects.push(existingObj);
            }
        } else {
            // If no matching existing object is found, add the new object (omitting fieldsToDelete) to addedObjects
            addedObjects.push(omit(newObj, fieldsToDelete));
        }
    }

    for (const remaining of existingMap.values()) {
        deletedObjects.push(existingObjKeyExtractor(remaining));
    }

    return { modifiedObjects, deletedObjects, addedObjects, unchangedObjects };
};


const hasNestedField = (obj, path) => {
    if (!obj || typeof path !== 'string') return false;

    return path.split('.').every(key => {
        if (obj && Object.prototype.hasOwnProperty.call(obj, key)) {
            obj = obj[key];
            return true;
        }
        return false;
    });
};